import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  getSponsorAnalytics,
  getSponsorRevenueReadiness,
  type SponsorAnalyticsRow,
  type SponsorReadinessRow,
} from "@/lib/sponsorPredictions";

export const Route = createFileRoute("/admin/sponsor-analytics")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sponsor Analytics · VerseSport Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SponsorAnalyticsPage,
});

function SponsorAnalyticsPage() {
  const { isSuperAdmin, isLoading } = useSuperAdmin();
  const [rows, setRows] = useState<SponsorAnalyticsRow[]>([]);
  const [ready, setReady] = useState<SponsorReadinessRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        setRows(await getSponsorAnalytics());
        setReady(await getSponsorRevenueReadiness());
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [isSuperAdmin]);

  if (isLoading) return <main className="p-4 text-sm">Verificando acceso…</main>;
  if (!isSuperAdmin) return (
    <main className="mx-auto max-w-md p-4 text-sm">
      <p className="font-bold">Acceso restringido</p>
      <Link to="/" className="text-primary underline">Volver</Link>
    </main>
  );

  return (
    <main className="mx-auto max-w-3xl space-y-4 px-3 py-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black">📈 Sponsor Analytics</h1>
          <p className="text-[12px] text-muted-foreground">Impresiones · clics · CTR · participaciones · por país.</p>
        </div>
        <Link to="/admin/sponsors" className="rounded-lg border border-border bg-background px-2 py-1 text-[11px] font-bold hover:bg-accent">
          ← Campañas
        </Link>
      </header>

      {err && <p className="rounded bg-destructive/10 p-2 text-[11px] text-destructive">{err}</p>}

      <section className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">Campañas</h2>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="text-muted-foreground">
              <tr>
                <th className="px-2 py-1">Marca</th>
                <th className="px-2 py-1">Impr.</th>
                <th className="px-2 py-1">Clics</th>
                <th className="px-2 py-1">Compl.</th>
                <th className="px-2 py-1">Únicos</th>
                <th className="px-2 py-1">CTR</th>
                <th className="px-2 py-1">Top país</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const top = Object.entries(r.by_country ?? {}).sort((a,b)=>b[1]-a[1])[0];
                return (
                  <tr key={r.campaign_id} className="border-t border-border/40">
                    <td className="px-2 py-1 font-black">{r.brand_name}</td>
                    <td className="px-2 py-1 tabular-nums">{r.impressions}</td>
                    <td className="px-2 py-1 tabular-nums">{r.clicks}</td>
                    <td className="px-2 py-1 tabular-nums">{r.completions}</td>
                    <td className="px-2 py-1 tabular-nums">{r.unique_users}</td>
                    <td className="px-2 py-1 tabular-nums font-bold text-primary">{r.ctr}%</td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground">{top ? `${top[0]} (${top[1]})` : "—"}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="px-2 py-4 text-center text-muted-foreground">Sin datos aún.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-background/60 p-3">
        <h2 className="text-sm font-black">🌍 Sponsor Revenue Readiness (0–100 · 30d)</h2>
        <p className="mt-1 text-[10px] text-muted-foreground">Score por país basado en impresiones, participaciones y usuarios únicos.</p>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {ready.map((r) => (
            <li key={r.country} className="rounded-lg border border-border/60 bg-background/70 p-2">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-black">{r.country}</span>
                <span className="text-[12px] font-black text-primary tabular-nums">{r.score}</span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${r.score}%` }} />
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">
                {r.impressions} impr · {r.completions} compl · {r.unique_users} únicos
              </div>
            </li>
          ))}
          {ready.length === 0 && <li className="text-[11px] text-muted-foreground">Sin actividad reciente.</li>}
        </ul>
      </section>
    </main>
  );
}